/**
 * Audio processing using Web Audio API
 * Handles microphone access and audio analysis
 */

export class AudioProcessor {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private microphone: MediaStreamAudioSourceNode | null = null;
  private mediaStream: MediaStream | null = null;
  private isActive = false;
  private bufferSize = 2048;
  private noiseGateThreshold = 0.01;

  async initialize(): Promise<boolean> {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false
        }
      });

      this.microphone = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = this.bufferSize * 2;
      this.analyser.smoothingTimeConstant = 0.3;

      this.microphone.connect(this.analyser);

      this.isActive = true;
      return true;
    } catch (error) {
      console.error('Audio initialization error:', error);
      throw new Error(this.getErrorMessage(error as Error));
    }
  }

  private getErrorMessage(error: Error): string {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return 'Microphone permission denied. Please allow microphone access.';
    } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return 'No microphone found. Please connect a microphone.';
    } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      return 'Microphone is already in use by another application.';
    }
    return 'Unable to access microphone: ' + error.message;
  }

  getAudioBuffer(): Float32Array | null {
    if (!this.analyser) return null;

    const buffer = new Float32Array(this.bufferSize);
    this.analyser.getFloatTimeDomainData(buffer);
    return buffer;
  }

  isSignalPresent(): boolean {
    const buffer = this.getAudioBuffer();
    if (!buffer) return false;

    const rms = Math.sqrt(buffer.reduce((sum, val) => sum + val * val, 0) / buffer.length);
    return rms > this.noiseGateThreshold;
  }

  getAudioLevel(): number {
    const buffer = this.getAudioBuffer();
    if (!buffer) return 0;

    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      const val = buffer[i];
      if (val !== undefined) {
        sum += val * val;
      }
    }
    return Math.sqrt(sum / buffer.length);
  }

  getSampleRate(): number {
    return this.audioContext ? this.audioContext.sampleRate : 44100;
  }

  stop(): void {
    this.isActive = false;

    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  isInitialized(): boolean {
    return this.isActive && !!this.audioContext && !!this.analyser;
  }
}
